import { Flex, Text } from '@chakra-ui/react'
import { Icon, IconButton, Spinner } from '@chakra-ui/react'
import { RefreshCw, RotateCw } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

interface UpdaterButtonProps {
  variant?: 'full' | 'icon'
  clearMessageDelayMs?: number
  hoverTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  statusTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  testUpdateDownloaded?: boolean
}

const UpdaterButton = ({
  variant = 'full',
  clearMessageDelayMs,
  hoverTooltipPlacement = 'bottom',
  statusTooltipPlacement = 'left',
  testUpdateDownloaded = false,
}: UpdaterButtonProps) => {
  const { t } = useTranslation()
  const {
    statusMessage,
    isUpdateDownloaded,
    isUpdateAvailable,
    isChecking,
    error,
    checkForUpdates,
    quitAndInstall,
    setClearMessageDelay,
  } = useElectronUpdaterContext()

  useEffect(() => {
    if (clearMessageDelayMs !== undefined) {
      setClearMessageDelay(clearMessageDelayMs)
    }
  }, [clearMessageDelayMs, setClearMessageDelay])

  if (error) {
    console.error(error)
  }

  const isUpdateReady = isUpdateDownloaded || testUpdateDownloaded

  const getTooltipContent = () => {
    if (isUpdateReady) {
      return t('updateCheck.restartAndUpdate')
    }
    return t('updateCheck.checkForUpdates')
  }

  const shouldShowStatusTooltip = () => {
    const hasActiveProcess = isChecking || isUpdateAvailable || isUpdateReady
    const hasStatusMessage = statusMessage && statusMessage.trim() !== ''
    return (hasActiveProcess || hasStatusMessage) && statusMessage
  }

  const getButtonColor = () => {
    if (isUpdateReady) return 'success.muted'
    if (isUpdateAvailable) return 'warning.muted'
    return 'gray.muted'
  }

  const spinnerColor = isUpdateAvailable ? 'warning.fg' : 'colorPalette.fg'

  if (!isElectron) return null

  if (variant === 'icon') {
    return (
      <Tooltip
        content={statusMessage}
        positioning={{ placement: statusTooltipPlacement }}
        open={!!shouldShowStatusTooltip()}
        closeOnPointerDown={false}
      >
        <span>
          <Tooltip
            content={getTooltipContent()}
            positioning={{ placement: hoverTooltipPlacement }}
            closeOnPointerDown={false}
          >
            <IconButton
              borderRadius={'10rem'}
              aria-label={
                isUpdateReady
                  ? t('updateCheck.restartAndUpdate')
                  : t('updateCheck.checkForUpdates')
              }
              size="md"
              variant="solid"
              colorPalette={isUpdateReady ? 'success' : 'brand'}
              bg="colorPalette.muted"
              _hover={{ bg: 'colorPalette.emphasized' }}
              _active={{ bg: 'colorPalette.emphasized' }}
              color={isUpdateReady ? 'white' : getButtonColor()}
              loading={isChecking}
              spinner={
                <Spinner
                  size="inherit"
                  borderWidth="0.125em"
                  color={spinnerColor}
                />
              }
              onClick={isUpdateReady ? quitAndInstall : checkForUpdates}
              disabled={isChecking}
            >
              <Icon aria-hidden="true">
                {isUpdateReady ? <RotateCw /> : <RefreshCw />}
              </Icon>
            </IconButton>
          </Tooltip>
        </span>
      </Tooltip>
    )
  }

  return (
    <Flex
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      marginBottom={1}
      marginTop={1}
    >
      <Text
        color={error ? 'red.solid' : 'fg.muted'}
        height={'auto'}
        maxHeight={'3rem'}
        lineHeight={'1.15rem'}
        overflowY={error ? 'auto' : 'hidden'}
        fontSize={'sm'}
      >
        {!error && (statusMessage || ' ')}
        {error && t('updateCheck.errorOccurred')}
      </Text>
      {isUpdateDownloaded && (
        <MangoButton
          marginTop={2}
          size={Size.sm}
          variant={MangoButtonVariant.Destructive_300}
          onClick={quitAndInstall}
        >
          {t('updateCheck.restartAndUpdate')}
        </MangoButton>
      )}
      {!isUpdateDownloaded && (
        <MangoButton
          disabled={isChecking}
          marginTop={2}
          size={Size.sm}
          variant={MangoButtonVariant.Success_300}
          onClick={checkForUpdates}
          isLoading={isChecking}
        >
          {t('updateCheck.checkForUpdates')}
        </MangoButton>
      )}
    </Flex>
  )
}

export default UpdaterButton
